import { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import { Delete, RestoreFromTrash } from "@mui/icons-material";
import { Page, PageHeader } from "../../layout/Page";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBlacklistedSenders,
  removeBlacklistedSender,
  reactivateBlacklistedSender,
} from "../../../api/client";
import { toast } from "../../../utils/notifications";
import { formatSingaporeDate, formatSingaporeTime } from "../../../utils/timezone";
import { queryKeys } from "../../../api/queryKeys";
import type { ErrorLike } from "../../../utils/errors";

interface BlacklistedSender {
  id: number;
  email_address: string;
  reason: string;
  blacklisted_by: string;
  blacklisted_at: string;
  rfq_id: number | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export default function BlacklistManagement() {
  const queryClient = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: "remove" | "reactivate";
    sender: BlacklistedSender | null;
  }>({ open: false, action: "remove", sender: null });

  // Fetch blacklisted senders
  const {
    data: blacklistedSenders = [],
    isLoading,
    error,
  } = useQuery<BlacklistedSender[]>({
    queryKey: queryKeys.blacklistedSenders.list(showInactive),
    queryFn: () => getBlacklistedSenders(!showInactive),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: (id: number) => removeBlacklistedSender(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Sender removed from blacklist",
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.blacklistedSenders.all });
      setConfirmDialog({ open: false, action: "remove", sender: null });
    },
    onError: (error: ErrorLike) => {
      toast({
        title: "Error",
        description: error.response?.data?.error || error.message || "Failed to remove sender",
        severity: "error",
      });
    },
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: (id: number) => reactivateBlacklistedSender(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Sender reactivated in blacklist",
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.blacklistedSenders.all });
      setConfirmDialog({ open: false, action: "reactivate", sender: null });
    },
    onError: (error: ErrorLike) => {
      toast({
        title: "Error",
        description: error.response?.data?.error || error.message || "Failed to reactivate sender",
        severity: "error",
      });
    },
  });

  const handleRemoveClick = (sender: BlacklistedSender) => {
    setConfirmDialog({ open: true, action: "remove", sender });
  };

  const handleReactivateClick = (sender: BlacklistedSender) => {
    setConfirmDialog({ open: true, action: "reactivate", sender });
  };

  const handleConfirm = () => {
    if (!confirmDialog.sender) return;

    if (confirmDialog.action === "remove") {
      removeMutation.mutate(confirmDialog.sender.id);
    } else {
      reactivateMutation.mutate(confirmDialog.sender.id);
    }
  };

  const handleCancel = () => {
    setConfirmDialog({ open: false, action: "remove", sender: null });
  };

  const tight = { width: "1%", whiteSpace: "nowrap" } as const;

  return (
    <Page width="full">
      <PageHeader
        title="Blacklist Management"
        subtitle="Manage email addresses that are blocked from RFQ processing"
        actions={
          <Button
            variant={showInactive ? "contained" : "outlined"}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? "Hide Inactive" : "Show All"}
          </Button>
        }
      />
      {error ? (
        <Alert severity="error">
          Failed to load blacklisted senders: {(error as Error).message}
        </Alert>
      ) : isLoading ? (
        <Paper sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Paper>
      ) : blacklistedSenders.length === 0 ? (
        <Alert severity="info">
          {showInactive
            ? "No blacklisted senders found"
            : "No active blacklisted senders. Click 'Show All' to see inactive entries."}
        </Alert>
      ) : (
        <Paper sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Email Address</TableCell>
                  <TableCell sx={tight}>Status</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell sx={tight}>Blacklisted By</TableCell>
                  <TableCell sx={tight}>Date</TableCell>
                  <TableCell sx={tight}>RFQ ID</TableCell>
                  <TableCell align="right" sx={tight}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blacklistedSenders.map((sender) => (
                  <TableRow
                    key={sender.id}
                    hover
                    sx={{
                      bgcolor: sender.is_active ? "inherit" : "action.hover",
                      opacity: sender.is_active ? 1 : 0.6,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                        {sender.email_address}
                      </Typography>
                      {sender.notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {sender.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tight}>
                      <Chip
                        label={sender.is_active ? "Active" : "Inactive"}
                        color={sender.is_active ? "error" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 240 }}>{sender.reason || "N/A"}</TableCell>
                    <TableCell sx={tight}>{sender.blacklisted_by || "Unknown"}</TableCell>
                    <TableCell sx={tight}>
                      <Typography variant="body2">
                        {formatSingaporeDate(sender.blacklisted_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatSingaporeTime(sender.blacklisted_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tight}>
                      {sender.rfq_id ? (
                        <Chip label={`#${sender.rfq_id}`} size="small" variant="outlined" />
                      ) : (
                        <Box component="span" sx={{ color: "text.disabled" }}>-</Box>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={tight}>
                      {sender.is_active ? (
                        <Tooltip title="Remove from blacklist">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleRemoveClick(sender)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Reactivate in blacklist">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleReactivateClick(sender)}
                          >
                            <RestoreFromTrash fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={handleCancel}>
        <DialogTitle>
          {confirmDialog.action === "remove"
            ? "Remove from Blacklist"
            : "Reactivate in Blacklist"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === "remove"
              ? `Are you sure you want to remove ${confirmDialog.sender?.email_address} from the blacklist? Emails from this sender will be processed again.`
              : `Are you sure you want to reactivate ${confirmDialog.sender?.email_address} in the blacklist? Emails from this sender will be blocked again.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            color={confirmDialog.action === "remove" ? "warning" : "primary"}
            variant="contained"
            disabled={removeMutation.isPending || reactivateMutation.isPending}
          >
            {removeMutation.isPending || reactivateMutation.isPending
              ? "Processing..."
              : confirmDialog.action === "remove"
              ? "Remove"
              : "Reactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

